import { useState, useEffect, useCallback } from "react";
import { authFetch } from "../../App";
import { format, subDays, addDays } from "date-fns";
import { de } from "date-fns/locale";
import { ChevronLeft, ChevronRight, Clock, MapPin, Coffee, Briefcase, Car, RefreshCw, Loader2, Plus, Trash2, X, FileText, ArrowRight, ArrowLeft } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const API = "/api";
const hm = (m) => { const v = Math.abs(m || 0); return `${Math.floor(v / 60)}:${(v % 60).toString().padStart(2, "0")}`; };

const HINFAHRT_TYPES = ["wohnort_projekt", "unterkunft_projekt"];
const RUECKFAHRT_TYPES = ["projekt_wohnort", "projekt_unterkunft"];

const TRIP_TYPE_OPTIONS = {
  hinfahrt: [
    { value: "wohnort_projekt", label: "Wohnort \u2192 Projekt" },
    { value: "unterkunft_projekt", label: "Unterkunft \u2192 Projekt" },
  ],
  rueckfahrt: [
    { value: "projekt_wohnort", label: "Projekt \u2192 Wohnort" },
    { value: "projekt_unterkunft", label: "Projekt \u2192 Unterkunft" },
  ],
};

/* ─── Small reusable trip card ─── */
function TripCard({ trip, onDelete }) {
  return (
    <div className="mb-2 bg-white/[0.04] rounded-xl border border-white/8 px-4 py-3 flex items-center gap-3" data-testid={`trip-${trip.id}`}>
      <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center flex-shrink-0">
        <Car className="w-4 h-4 text-blue-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-200">{trip.type_label}</p>
        <p className="text-[10px] text-gray-500 font-mono">
          {trip.start_time && trip.end_time ? `${trip.start_time} - ${trip.end_time} (${trip.duration_minutes} min)` : ""}
          {trip.km > 0 ? ` / ${trip.km} km` : ""}
        </p>
        {trip.note && <p className="text-[10px] text-gray-400 mt-0.5 italic">{trip.note}</p>}
      </div>
      <button onClick={() => onDelete(trip.id)} className="p-1.5 rounded-lg hover:bg-red-500/15" data-testid={`del-trip-${trip.id}`}>
        <Trash2 className="w-3.5 h-3.5 text-red-400" />
      </button>
    </div>
  );
}

/* ─── Inline trip form ─── */
function TripForm({ category, onSave, onCancel, saving }) {
  const types = TRIP_TYPE_OPTIONS[category] || [];
  const [form, setForm] = useState({ type: types[0]?.value || "", start_time: "", end_time: "", km: "", note: "" });

  const handleSave = () => {
    if (!form.start_time || !form.end_time) { toast.error("Start- und Endzeit eingeben"); return; }
    onSave(form);
  };

  return (
    <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-4 space-y-3 mb-2" data-testid={`trip-form-${category}`}>
      <div className="space-y-1.5">
        {types.map(t => (
          <button key={t.value} onClick={() => setForm(p => ({ ...p, type: t.value }))}
            className={`w-full h-9 rounded-lg border text-xs font-medium transition-all ${form.type === t.value ? "bg-blue-600 border-blue-500 text-white" : "bg-white/[0.04] border-white/10 text-gray-300"}`}
            data-testid={`trip-type-${t.value}`}>
            {t.label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] text-gray-500 block mb-0.5">Start</label>
          <input type="time" value={form.start_time} onChange={e => setForm(p => ({ ...p, start_time: e.target.value }))}
            className="w-full h-10 px-2 rounded-lg border border-white/10 bg-white/[0.04] text-white text-sm text-center outline-none" data-testid={`trip-${category}-start`} />
        </div>
        <div>
          <label className="text-[10px] text-gray-500 block mb-0.5">Ende</label>
          <input type="time" value={form.end_time} onChange={e => setForm(p => ({ ...p, end_time: e.target.value }))}
            className="w-full h-10 px-2 rounded-lg border border-white/10 bg-white/[0.04] text-white text-sm text-center outline-none" data-testid={`trip-${category}-end`} />
        </div>
        <div>
          <label className="text-[10px] text-gray-500 block mb-0.5">KM</label>
          <input type="number" min="0" placeholder="opt." value={form.km} onChange={e => setForm(p => ({ ...p, km: e.target.value }))}
            className="w-full h-10 px-2 rounded-lg border border-white/10 bg-white/[0.04] text-white text-sm text-center outline-none" data-testid={`trip-${category}-km`} />
        </div>
      </div>
      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving || !form.start_time || !form.end_time}
          className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.97] transition-all"
          data-testid={`save-trip-${category}`}>
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Car className="w-4 h-4" />} Speichern
        </button>
        <button onClick={onCancel} className="h-11 px-4 bg-white/[0.06] border border-white/10 text-gray-400 rounded-xl text-sm font-medium">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ─── Inline Arbeitszeit-Form (Beginn/Ende/Pause + Live-Berechnung) ─── */
function WorkTimeForm({ projects, onSave, onCancel, saving }) {
  const [form, setForm] = useState({
    project_id: projects[0]?.id || "",
    work_start: "",
    work_end: "",
    break_minutes: "30",
  });

  // Live-Berechnung Gesamtarbeitszeit
  const totalMinutes = (() => {
    if (!form.work_start || !form.work_end) return 0;
    const [sh, sm] = form.work_start.split(":").map(Number);
    const [eh, em] = form.work_end.split(":").map(Number);
    let mins = eh * 60 + em - (sh * 60 + sm);
    if (mins < 0) mins += 24 * 60;
    mins -= parseInt(form.break_minutes || "0", 10);
    return Math.max(0, mins);
  })();
  const totalH = Math.floor(totalMinutes / 60);
  const totalM = totalMinutes % 60;

  const handleSave = () => {
    if (!form.project_id) { toast.error("Bitte Projekt waehlen"); return; }
    if (!form.work_start || !form.work_end) { toast.error("Beginn und Ende eingeben"); return; }
    onSave(form);
  };

  return (
    <div className="bg-blue-500/5 border border-blue-500/15 rounded-xl p-4 space-y-3 mb-2" data-testid="worktime-form">
      {/* Projekt */}
      <div>
        <label className="text-[10px] text-gray-500 block mb-1">Projekt</label>
        <select
          value={form.project_id}
          onChange={(e) => setForm(p => ({ ...p, project_id: e.target.value }))}
          className="w-full h-10 px-2 rounded-lg border border-white/10 bg-white/[0.04] text-white text-sm outline-none"
          data-testid="worktime-project"
        >
          <option value="" className="bg-gray-900">Bitte Projekt waehlen</option>
          {projects.map(p => (
            <option key={p.id} value={p.id} className="bg-gray-900">
              {p.display_name || p.name}
            </option>
          ))}
        </select>
      </div>

      {/* Beginn / Ende / Pause */}
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="text-[10px] text-gray-500 block mb-0.5">Beginn</label>
          <input type="time" value={form.work_start} onChange={e => setForm(p => ({ ...p, work_start: e.target.value }))}
            className="w-full h-10 px-2 rounded-lg border border-white/10 bg-white/[0.04] text-white text-sm text-center outline-none" data-testid="worktime-start" />
        </div>
        <div>
          <label className="text-[10px] text-gray-500 block mb-0.5">Ende</label>
          <input type="time" value={form.work_end} onChange={e => setForm(p => ({ ...p, work_end: e.target.value }))}
            className="w-full h-10 px-2 rounded-lg border border-white/10 bg-white/[0.04] text-white text-sm text-center outline-none" data-testid="worktime-end" />
        </div>
        <div>
          <label className="text-[10px] text-gray-500 block mb-0.5">Pause (Min)</label>
          <input type="number" min="0" step="5" value={form.break_minutes} onChange={e => setForm(p => ({ ...p, break_minutes: e.target.value }))}
            className="w-full h-10 px-2 rounded-lg border border-white/10 bg-white/[0.04] text-white text-sm text-center outline-none" data-testid="worktime-break" />
        </div>
      </div>

      {/* Live-Berechnung Gesamtarbeitszeit */}
      <div className="flex items-center justify-between bg-white/[0.04] border border-white/8 rounded-lg px-3 py-2">
        <span className="text-[10px] text-gray-500 uppercase tracking-wider">Gesamtarbeitszeit</span>
        <span className="text-base font-mono font-bold text-emerald-400" data-testid="worktime-total">
          {form.work_start && form.work_end ? `${totalH}h ${String(totalM).padStart(2, "0")}m` : "—"}
        </span>
      </div>

      <div className="flex gap-2">
        <button onClick={handleSave} disabled={saving || !form.work_start || !form.work_end || !form.project_id}
          className="flex-1 h-11 bg-emerald-600 hover:bg-emerald-500 disabled:opacity-40 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.97] transition-all"
          data-testid="save-worktime">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />} In Stundenkonto eintragen
        </button>
        <button onClick={onCancel} className="h-11 px-4 bg-white/[0.06] border border-white/10 text-gray-400 rounded-xl text-sm font-medium">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ─── Note card ─── */
function NoteCard({ trip, onDelete }) {
  return (
    <div className="mb-2 bg-amber-500/5 rounded-xl border border-amber-500/15 px-4 py-3 flex items-start gap-3" data-testid={`note-${trip.id}`}>
      <div className="w-8 h-8 rounded-lg bg-amber-500/10 flex items-center justify-center flex-shrink-0 mt-0.5">
        <FileText className="w-4 h-4 text-amber-400" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-xs text-gray-200 leading-relaxed">{trip.note}</p>
        {trip.start_time && trip.end_time && (
          <p className="text-[10px] text-gray-500 font-mono mt-1">{trip.start_time} - {trip.end_time}</p>
        )}
      </div>
      <button onClick={() => onDelete(trip.id)} className="p-1.5 rounded-lg hover:bg-red-500/15" data-testid={`del-note-${trip.id}`}>
        <Trash2 className="w-3.5 h-3.5 text-red-400" />
      </button>
    </div>
  );
}

/* ─── Note form ─── */
function NoteForm({ onSave, onCancel, saving }) {
  const [note, setNote] = useState("");

  return (
    <div className="bg-amber-500/5 border border-amber-500/15 rounded-xl p-4 space-y-3 mb-2" data-testid="note-form">
      <textarea
        value={note} onChange={e => setNote(e.target.value)}
        placeholder="Notiz eingeben (z.B. Sonderfahrt, besondere Vorkommnisse...)"
        className="w-full h-24 px-3 py-2 rounded-lg border border-white/10 bg-white/[0.04] text-white text-sm outline-none resize-none placeholder:text-gray-600"
        data-testid="note-textarea"
      />
      <div className="flex gap-2">
        <button onClick={() => { if (!note.trim()) { toast.error("Bitte Notiz eingeben"); return; } onSave(note.trim()); }}
          disabled={saving || !note.trim()}
          className="flex-1 h-11 bg-amber-600 hover:bg-amber-500 disabled:opacity-40 text-white rounded-xl text-sm font-bold flex items-center justify-center gap-1.5 active:scale-[0.97] transition-all"
          data-testid="save-note-btn">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />} Notiz speichern
        </button>
        <button onClick={onCancel} className="h-11 px-4 bg-white/[0.06] border border-white/10 text-gray-400 rounded-xl text-sm font-medium">
          <X className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

/* ════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════ */
export default function MobileTagesuebersicht() {
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [entries, setEntries] = useState([]);
  const [dayData, setDayData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [user, setUser] = useState(null);
  const [trips, setTrips] = useState([]);
  const [showForm, setShowForm] = useState(null); // "hinfahrt" | "rueckfahrt" | "arbeitszeit" | "notiz" | null
  const [savingTrip, setSavingTrip] = useState(false);
  const [projects, setProjects] = useState([]);

  // Projects fuer Arbeitszeit-Form
  useEffect(() => {
    authFetch(`${API}/projects?status=aktiv`).then(r => r.ok ? r.json() : []).then(d => setProjects(Array.isArray(d) ? d : [])).catch(() => {});
  }, []);

  const loadData = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    else setRefreshing(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const [entriesRes, dayRes] = await Promise.all([
        authFetch(`${API}/time-entries/my?date=${dateStr}`),
        authFetch(`${API}/timesheet/summary?from=${dateStr}&to=${dateStr}`),
      ]);
      if (entriesRes.ok) {
        const data = await entriesRes.json();
        setEntries(Array.isArray(data) ? data : data.entries || [data].filter(d => d && (d.clock_in || d.work_start)));
      }
      if (dayRes.ok) {
        const summary = await dayRes.json();
        const days = summary.days || [];
        setDayData(days.length > 0 ? days[0] : null);
      }
    } catch {}
    setLoading(false);
    setRefreshing(false);
  }, [selectedDate]);

  useEffect(() => { loadData(); }, [loadData]);

  useEffect(() => {
    authFetch(`${API}/auth/me`).then(r => r.ok ? r.json() : null).then(u => { if (u) setUser(u); }).catch(() => {});
  }, []);

  const loadTrips = useCallback(async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await authFetch(`${API}/trips?date=${dateStr}`);
      if (res.ok) { const d = await res.json(); setTrips(d.trips || []); }
    } catch {}
  }, [selectedDate]);

  useEffect(() => { loadTrips(); }, [loadTrips]);

  /* ── Save Arbeitszeit (Beginn/Ende/Pause) ──
     POST /api/time-entries upserts auf user_id+date.
     Existierende travel-Felder werden vom Backend ueberschrieben (nicht gesetzt = null).
     Da der Mitarbeiter Hinfahrt/Rueckfahrt im trips-Collection (separat) erfasst,
     ist das ok – das Stundenkonto liest beide Quellen.
  */
  const handleSaveWorkTime = async (form) => {
    setSavingTrip(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await authFetch(`${API}/time-entries`, {
        method: "POST",
        body: JSON.stringify({
          date: dateStr,
          project_id: form.project_id,
          work_start: form.work_start,
          work_end: form.work_end,
          break_minutes: parseInt(form.break_minutes || "0", 10),
        }),
      });
      if (res.ok) { toast.success("Arbeitszeit eingetragen"); loadData(true); setShowForm(null); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Fehler"); }
    } catch { toast.error("Netzwerkfehler"); }
    setSavingTrip(false);
  };

  /* ── Save trip (Hin/Rueckfahrt) ── */
  const handleSaveTrip = async (form) => {
    setSavingTrip(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await authFetch(`${API}/trips`, {
        method: "POST",
        body: JSON.stringify({ ...form, date: dateStr, km: form.km ? parseFloat(form.km) : null }),
      });
      if (res.ok) { toast.success("Fahrt gespeichert"); loadTrips(); setShowForm(null); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Fehler"); }
    } catch { toast.error("Netzwerkfehler"); }
    setSavingTrip(false);
  };

  /* ── Save note ── */
  const handleSaveNote = async (noteText) => {
    setSavingTrip(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await authFetch(`${API}/trips`, {
        method: "POST",
        body: JSON.stringify({ type: "sonstiges", note: noteText, date: dateStr, start_time: "", end_time: "" }),
      });
      if (res.ok) { toast.success("Notiz gespeichert"); loadTrips(); setShowForm(null); }
      else { const err = await res.json().catch(() => ({})); toast.error(err.detail || "Fehler"); }
    } catch { toast.error("Netzwerkfehler"); }
    setSavingTrip(false);
  };

  const handleDeleteTrip = async (id) => {
    try {
      const res = await authFetch(`${API}/trips/${id}`, { method: "DELETE" });
      if (res.ok) { toast.success("Geloescht"); loadTrips(); }
    } catch {}
  };

  useEffect(() => {
    const interval = setInterval(() => loadData(true), 60000);
    return () => clearInterval(interval);
  }, [loadData]);

  const isToday = format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
  const totalWork = entries.reduce((s, e) => s + (e.work_time_minutes || 0), 0);
  const totalTravel = entries.reduce((s, e) => s + (e.total_travel_minutes || 0), 0);
  const totalKm = entries.reduce((s, e) => s + (e.total_kilometers || 0), 0);
  const totalBreak = entries.reduce((s, e) => s + (e.break_minutes || 0), 0);

  // Split trips into categories
  const hinfahrten = trips.filter(t => HINFAHRT_TYPES.includes(t.type));
  const rueckfahrten = trips.filter(t => RUECKFAHRT_TYPES.includes(t.type));
  const notizen = trips.filter(t => t.type === "sonstiges");

  // Add trip travel to total
  const tripMinutes = trips.reduce((s, t) => s + (t.duration_minutes || 0), 0);
  const tripKm = trips.reduce((s, t) => s + (t.km || 0), 0);

  const canAddTrips = user?.trip_entry_enabled && user?.role !== "nachunternehmer";

  return (
    <motion.div
      className="px-4 py-4 min-h-screen bg-[#0f1117] text-white"
      data-testid="m-tagesuebersicht"
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: "easeOut" }}
    >
      {/* ═══ DATE NAVIGATION ═══ */}
      <div className="flex items-center justify-between mb-4">
        <button onClick={() => setSelectedDate(subDays(selectedDate, 1))} className="w-10 h-10 flex items-center justify-center rounded-full active:bg-white/10 transition-colors" data-testid="m-tu-prev-day">
          <ChevronLeft className="w-5 h-5 text-gray-400" />
        </button>
        <button onClick={() => setSelectedDate(new Date())} className="text-center" data-testid="m-tu-today">
          <span className={`text-sm font-bold ${isToday ? "text-blue-400" : "text-white"}`}>
            {isToday ? "Heute" : format(selectedDate, "EEEE", { locale: de })}
          </span>
          <span className="text-[11px] text-gray-500 block">{format(selectedDate, "dd. MMMM yyyy", { locale: de })}</span>
        </button>
        <button onClick={() => setSelectedDate(addDays(selectedDate, 1))} className="w-10 h-10 flex items-center justify-center rounded-full active:bg-white/10 transition-colors" data-testid="m-tu-next-day">
          <ChevronRight className="w-5 h-5 text-gray-400" />
        </button>
      </div>

      {/* ═══ DAY SUMMARY CARD ═══ */}
      <AnimatePresence>
        {!loading && (entries.length > 0 || tripMinutes > 0) && (
          <motion.div
            className="bg-gradient-to-r from-blue-600/20 to-blue-600/5 border border-blue-500/20 rounded-xl p-4 mb-4"
            data-testid="m-tu-summary"
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
          >
            <div className="grid grid-cols-4 gap-2 text-center">
              <div>
                <Clock className="w-4 h-4 mx-auto mb-1 text-blue-400 opacity-60" />
                <div className="text-lg font-bold font-mono text-white">{hm(totalWork)}</div>
                <div className="text-[9px] text-gray-500 uppercase">Arbeit</div>
              </div>
              <div>
                <Car className="w-4 h-4 mx-auto mb-1 text-blue-400 opacity-60" />
                <div className="text-lg font-bold font-mono text-white">{hm(totalTravel + tripMinutes)}</div>
                <div className="text-[9px] text-gray-500 uppercase">Fahrt</div>
              </div>
              <div>
                <Coffee className="w-4 h-4 mx-auto mb-1 text-blue-400 opacity-60" />
                <div className="text-lg font-bold font-mono text-white">{totalBreak}m</div>
                <div className="text-[9px] text-gray-500 uppercase">Pause</div>
              </div>
              <div>
                <MapPin className="w-4 h-4 mx-auto mb-1 text-blue-400 opacity-60" />
                <div className="text-lg font-bold font-mono text-white">{Math.round(totalKm + tripKm)}</div>
                <div className="text-[9px] text-gray-500 uppercase">km</div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Refresh */}
      {!loading && (
        <div className="flex justify-end mb-2">
          <button onClick={() => loadData(true)} className="flex items-center gap-1 text-[10px] text-gray-500 active:text-gray-300 transition-colors" data-testid="m-tu-refresh">
            <RefreshCw className={`w-3 h-3 ${refreshing ? "animate-spin" : ""}`} />
            Aktualisieren
          </button>
        </div>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <Loader2 className="w-6 h-6 text-gray-500 animate-spin" />
        </div>
      ) : (
        <div className="space-y-2">

          {/* Empty-State: nur Absence-Hinweis (Urlaub/Krank/Feiertag) — sonst zeigt die
              Arbeitszeit-Section unten ja schon ihren eigenen "Arbeitszeit eintragen"-CTA. */}
          {entries.length === 0 && trips.length === 0 && dayData?.absence_type && (
            <div className="text-center py-6">
              <div className="w-12 h-12 rounded-2xl bg-white/[0.04] flex items-center justify-center mx-auto mb-2">
                <Briefcase className="w-5 h-5 text-gray-600" />
              </div>
              <p className="text-[12px] font-medium text-gray-400">
                {dayData?.absence_type === "urlaub" ? "Urlaub" :
                 dayData?.absence_type === "krank" ? "Krank" :
                 dayData?.absence_type === "feiertag" ? "Feiertag" :
                 dayData.absence_type}
              </p>
            </div>
          )}

          {/* ═══════════════════════════════════════
               1) HINFAHRT — above work entries
             ═══════════════════════════════════════ */}
          {canAddTrips && (
            <section data-testid="section-hinfahrt">
              <div className="flex items-center gap-2 mb-2">
                <ArrowRight className="w-3.5 h-3.5 text-emerald-400" />
                <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Hinfahrt</h3>
                {hinfahrten.length > 0 && (
                  <span className="text-[10px] font-mono text-emerald-400 ml-auto">{hm(hinfahrten.reduce((s, t) => s + (t.duration_minutes || 0), 0))}</span>
                )}
              </div>
              {hinfahrten.map(t => <TripCard key={t.id} trip={t} onDelete={handleDeleteTrip} />)}
              {showForm === "hinfahrt" ? (
                <TripForm category="hinfahrt" onSave={handleSaveTrip} onCancel={() => setShowForm(null)} saving={savingTrip} />
              ) : (
                <button onClick={() => setShowForm("hinfahrt")}
                  className="w-full h-10 bg-emerald-600/15 hover:bg-emerald-600/25 border border-emerald-500/20 text-emerald-400 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-[0.97] transition-all mb-1"
                  data-testid="btn-add-hinfahrt">
                  <Plus className="w-4 h-4" /> Hinfahrt eintragen
                </button>
              )}
            </section>
          )}

          {/* ═══════════════════════════════════════
               2) ARBEITSZEIT — work entries
             ═══════════════════════════════════════ */}
          <section data-testid="section-arbeitszeit">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="w-3.5 h-3.5 text-blue-400" />
              <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Arbeitszeit</h3>
              {entries.length > 0 && (
                <span className="text-[10px] font-mono text-blue-400 ml-auto">{hm(entries.reduce((s, e) => s + (e.work_time_minutes || 0), 0))}</span>
              )}
            </div>
            {/* Inline-Formular: Mitarbeiter traegt Beginn/Ende/Pause direkt ein → POST /api/time-entries → Stundenkonto */}
            {showForm === "arbeitszeit" && (
              <WorkTimeForm projects={projects} saving={savingTrip} onSave={handleSaveWorkTime} onCancel={() => setShowForm(null)} />
            )}
            {entries.map((entry, i) => {
              const start = entry.work_start || (entry.clock_in ? entry.clock_in.split("T")[1]?.substring(0, 5) : null);
              const end = entry.work_end || (entry.clock_out ? entry.clock_out.split("T")[1]?.substring(0, 5) : null);
              const workMin = entry.work_time_minutes || 0;
              const isActive = !end && start;

              return (
                <motion.div
                  key={i}
                  className="relative mb-2"
                  data-testid={`m-tu-entry-${i}`}
                  initial={{ opacity: 0, x: -16 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ duration: 0.3, delay: 0.1 + i * 0.06, ease: "easeOut" }}
                >
                  <div className={`bg-white/[0.04] rounded-xl border overflow-hidden transition-all ${isActive ? "border-green-500/30 shadow-sm shadow-green-500/5" : "border-white/8"}`}>
                    <div className="flex items-center gap-2.5 px-4 py-2.5 border-b border-white/5 bg-white/[0.02]">
                      <div className="w-2 h-7 rounded-full shrink-0" style={{ backgroundColor: entry.project_color || "#6B7280" }} />
                      <div className="flex-1 min-w-0">
                        <span className="text-[12px] font-semibold text-gray-200 truncate block">{entry.project_name || "Projekt"}</span>
                      </div>
                      {isActive && (
                        <span className="px-2 py-0.5 bg-green-500/20 text-green-400 text-[9px] font-bold rounded-full uppercase tracking-wider">Live</span>
                      )}
                    </div>
                    <div className="grid grid-cols-3 gap-0 divide-x divide-white/5">
                      <div className="text-center py-3">
                        <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-0.5">Start</p>
                        <p className="text-base font-mono font-bold text-white">{start || "\u2014"}</p>
                      </div>
                      <div className="text-center py-3">
                        <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-0.5">Ende</p>
                        <p className={`text-base font-mono font-bold ${isActive ? "text-green-400" : "text-white"}`}>{end || (isActive ? "laeuft" : "\u2014")}</p>
                      </div>
                      <div className="text-center py-3">
                        <p className="text-[9px] text-gray-500 uppercase tracking-wider mb-0.5">Netto</p>
                        <p className="text-base font-mono font-bold text-blue-400">{hm(workMin)}</p>
                      </div>
                    </div>
                    {(entry.total_travel_minutes > 0 || entry.total_kilometers > 0 || entry.break_minutes > 0) && (
                      <div className="flex items-center gap-4 px-4 py-2 border-t border-white/5 bg-white/[0.02]">
                        {entry.break_minutes > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-gray-500">
                            <Coffee className="w-3 h-3" /> {entry.break_minutes}m Pause
                          </span>
                        )}
                        {entry.total_travel_minutes > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-gray-500">
                            <Car className="w-3 h-3" /> {hm(entry.total_travel_minutes)} Fahrt
                          </span>
                        )}
                        {entry.total_kilometers > 0 && (
                          <span className="flex items-center gap-1 text-[10px] text-gray-500">
                            <MapPin className="w-3 h-3" /> {Math.round(entry.total_kilometers)} km
                          </span>
                        )}
                      </div>
                    )}
                  </div>
                </motion.div>
              );
            })}
            {/* CTA: Arbeitszeit eintragen */}
            {showForm !== "arbeitszeit" && (
              <button
                onClick={() => setShowForm("arbeitszeit")}
                className="w-full h-11 mt-1 bg-white/[0.04] hover:bg-white/[0.07] border border-dashed border-white/15 text-gray-400 rounded-xl text-xs font-medium flex items-center justify-center gap-1.5 transition-all"
                data-testid="btn-add-arbeitszeit"
              >
                <Plus className="w-4 h-4" /> Arbeitszeit eintragen
              </button>
            )}
          </section>
          {canAddTrips && (
            <section data-testid="section-rueckfahrt">
              <div className="flex items-center gap-2 mb-2">
                <ArrowLeft className="w-3.5 h-3.5 text-orange-400" />
                <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Rueckfahrt</h3>
                {rueckfahrten.length > 0 && (
                  <span className="text-[10px] font-mono text-orange-400 ml-auto">{hm(rueckfahrten.reduce((s, t) => s + (t.duration_minutes || 0), 0))}</span>
                )}
              </div>
              {rueckfahrten.map(t => <TripCard key={t.id} trip={t} onDelete={handleDeleteTrip} />)}
              {showForm === "rueckfahrt" ? (
                <TripForm category="rueckfahrt" onSave={handleSaveTrip} onCancel={() => setShowForm(null)} saving={savingTrip} />
              ) : (
                <button onClick={() => setShowForm("rueckfahrt")}
                  className="w-full h-10 bg-orange-600/15 hover:bg-orange-600/25 border border-orange-500/20 text-orange-400 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-[0.97] transition-all mb-1"
                  data-testid="btn-add-rueckfahrt">
                  <Plus className="w-4 h-4" /> Rueckfahrt eintragen
                </button>
              )}
            </section>
          )}

          {/* ═══════════════════════════════════════
               4) NOTIZEN — at the bottom
             ═══════════════════════════════════════ */}
          {canAddTrips && (
            <section data-testid="section-notizen" className="mt-2">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="w-3.5 h-3.5 text-amber-400" />
                <h3 className="text-[11px] font-semibold text-gray-500 uppercase tracking-wider">Notizen</h3>
              </div>
              {notizen.map(t => <NoteCard key={t.id} trip={t} onDelete={handleDeleteTrip} />)}
              {showForm === "notiz" ? (
                <NoteForm onSave={handleSaveNote} onCancel={() => setShowForm(null)} saving={savingTrip} />
              ) : (
                <button onClick={() => setShowForm("notiz")}
                  className="w-full h-10 bg-amber-600/15 hover:bg-amber-600/25 border border-amber-500/20 text-amber-400 rounded-xl text-xs font-semibold flex items-center justify-center gap-1.5 active:scale-[0.97] transition-all"
                  data-testid="btn-add-notiz">
                  <Plus className="w-4 h-4" /> Notiz hinzufuegen
                </button>
              )}
            </section>
          )}

        </div>
      )}
    </motion.div>
  );
}
